import React from "react";
import { motion } from "framer-motion";

/**
 * Animated version of the Prayosha Construction logo.
 * 
 * Animation sequence:
 * 1. Orange background scales in
 * 2. White architectural lines draw in via pathLength (stroke animation)
 * 3. After drawing completes, the solid filled version fades in with a glow
 */

// The original white architectural path from logo-block.svg
const LOGO_PATH = "M 124.777344 114.355469 L 124.777344 33.425781 L 43.269531 114.898438 L 43.269531 229.855469 L 51.117188 229.855469 L 51.117188 118.183594 L 116.890625 52.414062 L 116.890625 110.875 L 84.992188 96.6875 L 84.992188 229.855469 L 124.777344 229.855469 L 124.777344 132.800781 L 116.929688 128.742188 L 116.929688 222.042969 L 92.878906 222.042969 L 92.878906 108.75 L 116.929688 119.460938 L 124.777344 122.941406 L 245.84375 176.765625 L 245.84375 229.855469 L 253.691406 229.855469 L 253.691406 171.699219 Z";

const DRAW_DURATION = 2.5;
const DRAW_DELAY = 0.6;
const FILL_DELAY = DRAW_DELAY + DRAW_DURATION + 0.2;

export function AnimatedLogo({ width = 200 }) {
    return (
        <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={width}
            height={width}
            viewBox="0 0 375 375"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
        >
            <defs>
                {/* Glow filter for the fill reveal */}
                <filter id="logoGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Orange rounded-rect background */}
            <motion.rect
                x="39.875"
                y="32.59"
                width="295.25"
                height="304.48"
                rx="11.25"
                ry="11.25"
                fill="#cf5e29"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            />

            <g transform="translate(39, 32)">
                {/* Phase 1: Stroke-only path that draws in via pathLength */}
                <motion.path
                    d={LOGO_PATH}
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    initial={{ pathLength: 0, opacity: 1 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{
                        pathLength: {
                            duration: DRAW_DURATION,
                            delay: DRAW_DELAY,
                            ease: "easeInOut",
                        },
                    }}
                />

                {/* Phase 2: Filled path that fades in after drawing, with glow */}
                <motion.path
                    d={LOGO_PATH}
                    fill="white"
                    fillRule="nonzero"
                    stroke="none"
                    filter="url(#logoGlow)"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0, 1] }}
                    transition={{
                        duration: 0.8,
                        delay: FILL_DELAY,
                        ease: "easeOut",
                        times: [0, 0.1, 1],
                    }}
                />

                {/* Phase 2b: Brief bright glow flash when fill appears */}
                <motion.path
                    d={LOGO_PATH}
                    fill="white"
                    fillRule="nonzero"
                    stroke="none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.6, 0] }}
                    transition={{
                        duration: 1.2,
                        delay: FILL_DELAY,
                        ease: "easeInOut",
                    }}
                    style={{ filter: "blur(8px)" }}
                />
            </g>

            {/* Outer glow pulse on the border when fill completes */}
            <motion.rect
                x="39.875"
                y="32.59"
                width="295.25"
                height="304.48"
                rx="11.25"
                ry="11.25"
                fill="none"
                stroke="rgba(207, 94, 41, 0.6)"
                strokeWidth="0"
                initial={{ strokeWidth: 0, opacity: 0 }}
                animate={{ strokeWidth: [0, 6, 0], opacity: [0, 0.5, 0] }}
                transition={{
                    duration: 1.5,
                    delay: FILL_DELAY,
                    ease: "easeInOut",
                }}
            />
        </motion.svg>
    );
}
